import { Suspense, useEffect, useLayoutEffect, useState } from 'react'
import { Route, Routes, useLocation } from 'react-router-dom'
import { ThemeProvider as StyledThemeProvider } from 'styled-components'

import { LoadingCircular } from 'components/atoms/LoadingCircular'
import { ModalWindowSizeWarning } from 'components/atoms/ModalWindowSizeWarning'
import { LazyToaster } from 'components/LazyToaster'
import { PrivateRoutes } from 'components/PrivateRoutes'
import { RefreshToken } from 'components/RefreshToken'
import { useFeatureFlag } from 'hooks/useFeatureFlag'
import LoginPage from 'pages/auth/login'
import ResetPasswordPage from 'pages/auth/resetPassword'
import SetPasswordPage from 'pages/auth/setPassword'
import NotFoundPage from 'pages/errorPages/notFound'
import {
  AdminPanel,
  AiContextManagement,
  AiLimitsManagement,
  Analytics,
  AssetsAddPage,
  AssetsEditPage,
  AssetsPage,
  AssetsSearchPage,
  BrandContextAddPage,
  BrandContextEditPage,
  BrandsManagementPage,
  CardPreviewPage,
  ChristmasCardPage,
  ExtendedContextAddPage,
  ExtendedContextEditPage,
  IFramePreviewPage,
  ImageEditorPage,
  LandingPageAddPage,
  LandingPageAssetAddPage,
  LandingPageAssetEditPage,
  LandingPageEditPage,
  LandingPageFontsAdminPage,
  LandingPagePreviewPage,
  LandingPagesAdminPage,
  LandingPagesAssetsPage,
  LandingPagesPage,
  PackageDetails,
  PackagesPage,
  ProductAddPage,
  ProductEditPage,
  ProductsPage,
  ReviewAccessPage,
  ReviewSessionDetailPage,
  ReviewSessionRoomPage,
  ReviewsPage,
  UserCreate,
  UserEdit,
  UserList
} from 'pages/lazyPages'
import { ModalPrivacyPolicyAcceptance } from 'pages/privacyPolicy/ModalPrivacyPolicyAcceptance'
import PrivacyPolicy from 'pages/privacyPolicy/PrivacyPolicy'
import GlobalStyles from 'utils/globalStyles'
import { PATHS } from 'utils/paths'
import { MAX_WINDOW_WIDTH } from 'utils/shared'
import theme from 'utils/theme'

function App() {
  const [openSizeWarning, setOpenSizeWarning] = useState(false)
  const { pathname } = useLocation()
  const isLandingPagesEnabled = useFeatureFlag('landingPages')

  // Prevent Enter in inputs/select from submitting forms (e.g. product edit) – allow on login/auth so Enter can submit
  const isAuthRoute =
    pathname === PATHS.home ||
    pathname === PATHS.login ||
    pathname === PATHS.resetPassword ||
    pathname === PATHS.setPassword

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Enter') return
      if (isAuthRoute) return
      const tag = (e.target as HTMLElement)?.tagName

      if (tag === 'INPUT' || tag === 'SELECT') {
        e.preventDefault()
      }
    }

    document.addEventListener('keydown', handler, true)

    return () => document.removeEventListener('keydown', handler, true)
  }, [isAuthRoute])

  useLayoutEffect(() => {
    if (
      pathname === PATHS.cardPreview ||
      pathname === PATHS.landingPagePreview ||
      pathname.startsWith('/iframe/') ||
      PATHS.christmasCards.includes(pathname.toLowerCase())
    )
      return

    const updateSize = () => {
      if (window.innerWidth < MAX_WINDOW_WIDTH) {
        setOpenSizeWarning(true)
      }

      if (window.innerWidth >= MAX_WINDOW_WIDTH) {
        setOpenSizeWarning(false)
      }
    }

    window.addEventListener('resize', updateSize)
    updateSize()

    return () => window.removeEventListener('resize', updateSize)
  }, [])

  return (
    <StyledThemeProvider theme={theme}>
      <LazyToaster />
      <GlobalStyles />
      <RefreshToken />
      <ModalWindowSizeWarning open={openSizeWarning} />
      <Suspense fallback={<LoadingCircular withoutPadding />}>
        <Routes>
        {/* PUBLIC ROUTERS */}
        <Route path={PATHS.home} element={<LoginPage />} />
        <Route path={PATHS.login} element={<LoginPage />} />
        <Route path={PATHS.resetPassword} element={<ResetPasswordPage />} />
        <Route path={PATHS.setPassword} element={<SetPasswordPage />} />
        <Route path={PATHS.cardPreview} element={<CardPreviewPage />} />
        <Route path={PATHS.privacyPolicy} element={<PrivacyPolicy />} />
        <Route path={PATHS.packageDetails} element={<PackageDetails />} />
        <Route path={PATHS.iframePreview} element={<IFramePreviewPage />} />
        <Route path={PATHS.reviewAccess} element={<ReviewAccessPage />} />
        <Route
          path={PATHS.reviewSessionAccess}
          element={<ReviewAccessPage />}
        />
        {PATHS.christmasCards.map(card => (
          <Route key={card} path={card} element={<ChristmasCardPage />} />
        ))}
        {isLandingPagesEnabled && (
          <Route
            path={PATHS.landingPagePreview}
            element={<LandingPagePreviewPage />}
          />
        )}
        {/* PRIVATE ROUTERS */}
        <Route element={<PrivateRoutes />}>
          <Route
            path={PATHS.policyAcceptance}
            element={<ModalPrivacyPolicyAcceptance />}
          />
          <Route path={PATHS.products} element={<ProductsPage />} />
          <Route path={PATHS.assets} element={<AssetsPage />} />
          <Route path={PATHS.assetsAdd} element={<AssetsAddPage />} />
          <Route path={PATHS.assetsEdit} element={<AssetsEditPage />} />
          <Route path={PATHS.assetsSearch} element={<AssetsSearchPage />} />
          <Route path={PATHS.packages} element={<PackagesPage />} />
          <Route path={PATHS.iframes} element={<PackagesPage />} />
          <Route
            path={PATHS.reviewSessionRoom}
            element={<ReviewSessionRoomPage />}
          />
          <Route
            path={PATHS.reviewSessionDetail}
            element={<ReviewSessionDetailPage />}
          />
          <Route path={PATHS.reviewsLandingPages} element={<ReviewsPage />} />
          <Route path={PATHS.reviewsProducts} element={<ReviewsPage />} />
          <Route path={PATHS.reviews} element={<ReviewsPage />} />
          <Route path={PATHS.productAdd} element={<ProductAddPage />} />
          <Route path={PATHS.productEdit} element={<ProductEditPage />} />
          <Route path={PATHS.admin} element={<AdminPanel />} />
          {isLandingPagesEnabled && (
            <>
              <Route
                key={PATHS.landingPagesAdmin}
                path={PATHS.landingPagesAdmin}
                element={<LandingPagesAdminPage />}
              />
              <Route
                key={PATHS.landingPageFonts}
                path={PATHS.landingPageFonts}
                element={<LandingPageFontsAdminPage />}
              />
            </>
          )}
          <Route
            path={PATHS.brandsManagement}
            element={<BrandsManagementPage />}
          />
          <Route path={PATHS.userManagement} element={<UserList />} />
          <Route path={PATHS.userCreate} element={<UserCreate />} />
          <Route path={PATHS.userEdit} element={<UserEdit />} />
          <Route
            path={PATHS.aiLimitsManagement}
            element={<AiLimitsManagement />}
          />
          <Route path={PATHS.aiContext} element={<AiContextManagement />} />
          <Route path={PATHS.analytics} element={<Analytics />} />
          <Route
            path={PATHS.brandContextAdd}
            element={<BrandContextAddPage />}
          />
          <Route
            path={PATHS.brandContextEdit}
            element={<BrandContextEditPage />}
          />
          <Route
            path={PATHS.extendedContextAdd}
            element={<ExtendedContextAddPage />}
          />
          <Route
            path={PATHS.extendedContextEdit}
            element={<ExtendedContextEditPage />}
          />
          <Route path={PATHS.imageEditor} element={<ImageEditorPage />} />
          {isLandingPagesEnabled && (
            <>
              <Route
                key={PATHS.landingPages}
                path={PATHS.landingPages}
                element={<LandingPagesPage />}
              />
              <Route
                key={PATHS.landingPagesPackages}
                path={PATHS.landingPagesPackages}
                element={<PackagesPage />}
              />
              <Route
                key={PATHS.landingPagesIframes}
                path={PATHS.landingPagesIframes}
                element={<PackagesPage />}
              />
              <Route
                key={PATHS.landingPagesAdd}
                path={PATHS.landingPagesAdd}
                element={<LandingPageAddPage />}
              />
              <Route
                key={PATHS.landingPagesEdit}
                path={PATHS.landingPagesEdit}
                element={<LandingPageEditPage />}
              />
              <Route
                key={PATHS.landingPagesAssets}
                path={PATHS.landingPagesAssets}
                element={<LandingPagesAssetsPage />}
              />
              <Route
                key={PATHS.landingPagesAssetsAdd}
                path={PATHS.landingPagesAssetsAdd}
                element={<LandingPageAssetAddPage />}
              />
              <Route
                key={PATHS.landingPagesAssetsEdit}
                path={PATHS.landingPagesAssetsEdit}
                element={<LandingPageAssetEditPage />}
              />
            </>
          )}
        </Route>

        {/* 404 - NotFound*/}
        <Route path={PATHS.notFoundPage} element={<NotFoundPage />} />
        <Route path='*' element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </StyledThemeProvider>
  )
}

export default App
